import type { ViewContainerRef } from '@angular/core';
import { Injectable, inject } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { map, of, switchMap, take, tap } from 'rxjs';

import { filterBoolean } from '@core/common-legacy';
import { FeatureSpecSelectors } from '@core/feature-spec-data';
import { FeatureCode } from '@core/feature-spec-types';
import { I18nService } from '@core/i18n-legacy';
import type { LocationJobs } from '@core/locations-data';
import { LocationActions, LocationJobsActions, LocationJobsSelectors } from '@core/locations-data';
import { OfferStatus } from '@core/offers-types';
import { DialogService, DialogWidth, NotificationsController } from '@core/user-interface-legacy';

import { ManageLocationDialogControllerService } from '../dialogs/manage-location-dialog/manage-location-dialog.controller';
import type { ManageLocationCtrlData } from '../dialogs/manage-location-dialog/manage-location-dialog.types';

@Injectable({ providedIn: 'root' })
export class LocationsActionsService {
  private readonly dialogService = inject(DialogService);
  private readonly i18n = inject(I18nService);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly notifications = inject(NotificationsController);
  private readonly manageLocationDialogController = inject(ManageLocationDialogControllerService);

  public newLocation$(vcRef: ViewContainerRef, option?: ManageLocationCtrlData) {
    return this.manageLocationDialogController.openNewLocationDialog$(option || {}, vcRef);
  }

  public editLocation$(vcRef: ViewContainerRef, option: ManageLocationCtrlData) {
    return this.manageLocationDialogController.openEditLocationDialog$(option, vcRef);
  }

  public archive$(
    id: number,
    activeOffersCount: number,
    activeRequisitionsCount: number,
  ): Observable<boolean> {
    this.store.dispatch(LocationJobsActions.fetchOne({ payload: { id } }));

    return this.store.select(LocationJobsSelectors.byId(id)).pipe(
      filterBoolean,
      take(1),
      switchMap((jobs: LocationJobs) => {
        const jobsWithOnlyOneLocationCount = jobs.filter(
          job =>
            [OfferStatus.Closed, OfferStatus.Internal, OfferStatus.Published].includes(
              <OfferStatus>job.status,
            ) && job.locations.filter(location => !location.archivedAt).length === 1,
        ).length;
        const canBeArchived = !jobsWithOnlyOneLocationCount;
        const showConfirmDialog = !!(activeOffersCount || activeRequisitionsCount);

        if (canBeArchived) {
          return this.handleArchive$(id, showConfirmDialog).pipe(map(() => true));
        } else {
          this.notifications.error({
            message: this.i18n.translate('app.locations.notifications.archive_blocked.message', {
              count: jobsWithOnlyOneLocationCount,
            }),
            title: this.i18n.translate('app.locations.notifications.archive_blocked.title'),
          });
          return of(false);
        }
      }),
    );
  }

  public retrieve$(
    id: number,
    activeOffersCount: number,
    activeRequisitionsCount: number,
  ): Observable<boolean> {
    const confirmation$ = this.store
      .select(FeatureSpecSelectors.forCode(FeatureCode.Requisitions))
      .pipe(
        switchMap(requisitionsFeature =>
          activeOffersCount || (activeRequisitionsCount && !!requisitionsFeature?.enabled)
            ? this.getConfirmRetrieveDialog$(activeOffersCount, activeRequisitionsCount)
            : of(true),
        ),
      );

    return confirmation$.pipe(
      take(1),
      filterBoolean,
      tap(() => {
        this.store.dispatch(LocationActions.retrieve({ payload: { id } }));
      }),
      switchMap(() =>
        this.actions$.pipe(ofType(LocationActions.retrieveSuccess), take(1)).pipe(
          tap(() => {
            this.notifications.success(
              this.i18n.translate('app.locations.notifications.retrieve_success'),
            );
          }),
        ),
      ),
      map(() => true),
    );
  }

  public duplicate$(id: number): Observable<boolean> {
    this.store.dispatch(LocationActions.duplicate({ payload: { id } }));

    return this.actions$.pipe(ofType(LocationActions.duplicateSuccess), take(1)).pipe(
      tap(() => {
        this.notifications.success(
          this.i18n.translate('app.locations.notifications.duplicate_success'),
        );
      }),
      map(() => true),
    );
  }

  public delete$({
    id,
    name,
    activeOffersCount,
    activeRequisitionsCount,
  }: {
    id: number;
    name: string;
    activeOffersCount: number;
    activeRequisitionsCount: number;
  }): Observable<boolean> {
    this.store.dispatch(LocationJobsActions.fetchOne({ payload: { id } }));

    return this.store.select(LocationJobsSelectors.byId(id)).pipe(
      filterBoolean,
      take(1),
      switchMap((jobs: LocationJobs) => {
        const showConfirmDialog = !!(activeOffersCount || activeRequisitionsCount);
        return this.handleDelete$(id, name, showConfirmDialog).pipe(map(() => true));
      }),
    );
  }

  public fetchOne$(id: number) {
    this.store.dispatch(LocationActions.fetchOne({ payload: { id } }));
  }

  private handleArchive$(id: number, showConfirmDialog: boolean) {
    return this.store.select(FeatureSpecSelectors.forCode(FeatureCode.Requisitions)).pipe(
      switchMap(requisitionsFeature =>
        showConfirmDialog
          ? this.dialogService.warning({
              width: DialogWidth.SmallXX,
              message: this.getWarningArchiveMessage(!!requisitionsFeature?.enabled),
              title: this.i18n.translate('app.locations.confirm_archive.title'),
              agreeLabel: this.i18n.translate('app.locations.confirm_archive.agree_label'),
            })
          : of(true),
      ),
      take(1),
      filterBoolean,
      tap(() => {
        this.store.dispatch(
          LocationActions.archive({
            payload: { id },
          }),
        );
      }),
      switchMap(() =>
        this.actions$.pipe(ofType(LocationActions.archiveSuccess), take(1)).pipe(
          tap(() => {
            this.notifications.success(
              this.i18n.translate('app.locations.notifications.archive_success'),
            );
          }),
        ),
      ),
    );
  }

  private handleDelete$(id: number, name: string, showConfirmDialog: boolean) {
    return this.store.select(FeatureSpecSelectors.forCode(FeatureCode.Requisitions)).pipe(
      switchMap(requisitionsFeature =>
        showConfirmDialog
          ? this.dialogService.error({
              width: DialogWidth.SmallXX,
              message: this.i18n.translate(
                !!requisitionsFeature?.enabled
                  ? 'app.locations.confirm_delete.message_with_requisitions'
                  : 'app.locations.confirm_delete.message',
                { name },
              ),
              title: this.i18n.translate('app.locations.confirm_delete.title'),
              agreeLabel: this.i18n.translate('app.locations.confirm_delete.agree_label'),
              checkboxes: [
                {
                  title: this.i18n.translate('app.locations.confirm_delete.checkbox'),
                },
              ],
              safeFooter: true,
            })
          : of(true),
      ),
      take(1),
      filterBoolean,
      tap(() => {
        this.store.dispatch(
          LocationActions.delete({
            payload: { data: id },
          }),
        );
      }),
      switchMap(() =>
        this.actions$.pipe(ofType(LocationActions.deleteSuccess), take(1)).pipe(
          tap(() => {
            this.notifications.success(
              this.i18n.translate('app.locations.notifications.delete_success'),
            );
          }),
        ),
      ),
    );
  }

  private getConfirmRetrieveDialog$(activeOffersCount: number, activeRequisitionsCount: number) {
    let message = '';
    let locationUses = '';
    const jobs = this.i18n.translate('app.plurals.job', { count: activeOffersCount });
    const requisitions = this.i18n.translate('app.plurals.requisition', {
      count: activeRequisitionsCount,
    });
    const and = this.i18n.translate('app.common.and');
    if (activeOffersCount && activeRequisitionsCount) {
      locationUses = `${activeOffersCount} ${jobs} ${and} ${activeRequisitionsCount} ${requisitions}`;
      message = this.i18n.translate('app.locations.confirm_retrieve.message.content', {
        count: activeOffersCount,
        locationUses,
      });
    } else if (activeOffersCount) {
      locationUses = `${activeOffersCount} ${jobs}`;
      message = this.i18n.translate('app.locations.confirm_retrieve.message.content', {
        count: activeOffersCount,
        locationUses,
      });
    } else if (activeRequisitionsCount) {
      message = this.i18n.translate('app.locations.confirm_retrieve.message.requisitions', {
        count: activeRequisitionsCount,
      });
    }

    return this.dialogService
      .information({
        width: DialogWidth.SmallXX,
        message,
        title: this.i18n.translate('app.locations.confirm_retrieve.title'),
        agreeLabel: this.i18n.translate('app.locations.confirm_retrieve.agree_label'),
      })
      .pipe(filterBoolean);
  }

  private getWarningArchiveMessage(requisitionsFeatureEnabled: boolean) {
    let message = this.i18n.translate('app.locations.confirm_archive.message');
    if (requisitionsFeatureEnabled) {
      message =
        message + ` ${this.i18n.translate('app.locations.confirm_archive.message_requisition')}`;
    }
    return message;
  }
}
